import CloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Header, Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { Modal } from "../../modal";
import * as css from "./hsa-learn-more-modal.module.css";
import { hsaLearnMoreSteps } from "./hsa-learn-more-steps";

const ASSET_BASE = "https://assets.getredo.com/conversion/hsa/";

/**
 * The shared "how paying with an HSA/FSA works" walkthrough, opened from the
 * PDP badge and the cart widget.
 *
 * `requiresLmn` inserts the health-assessment step and renumbers the rest —
 * the PDP passes it per selected variant, the cart passes it when ANY eligible
 * line requires a Letter of Medical Necessity. Callers must render this inside
 * a `ModalRootContext` whose container lives outside the widget's own shadow
 * root, or the dialog is clipped by the theme's cart/product markup.
 */
export const HsaLearnMoreModal = memo(function HsaLearnMoreModal({
  requiresLmn,
  open,
  onClose,
}: {
  requiresLmn: boolean;
  open: boolean;
  onClose: () => void;
}) {
  const steps = hsaLearnMoreSteps(requiresLmn);

  return (
    <Modal onClose={onClose} open={open}>
      <Flex
        className={css.modalContent}
        dir="column"
        gap="3xl"
        overflowY="auto"
        p="3xl"
      >
        <Flex dir="column" gap="none">
          <Flex align="center" dir="row" justify="flex-end">
            <RedoButton
              aria-label="Close"
              hierarchy="tertiary"
              IconLeading={CloseIcon}
              onClick={onClose}
              size="md"
            />
          </Flex>
          <Flex dir="column" gap="md">
            <Flex dir="column" gap="sm">
              <Header as="h2" fontSize="sm" fontWeight="medium">
                Maximize your savings.
                <br />
                Pay with FSA &amp; HSA.
              </Header>
            </Flex>
            <Text fontSize="lg">
              Here&apos;s how to use your HSA/FSA funds.
            </Text>
          </Flex>
        </Flex>
        {steps.map((step, index) => (
          <Flex dir="column" gap="sm" key={index}>
            <span className={css.stepPill}>Step {index + 1}</span>
            <Header as="h3" fontSize="xxs">
              {step.title}
            </Header>
            <div className={css.stepImageWell}>
              <img
                alt=""
                className={css.stepImage}
                src={`${ASSET_BASE}${step.image}`}
              />
            </div>
          </Flex>
        ))}
        <RedoButton
          hierarchy="primary"
          onClick={onClose}
          size="lg"
          text="Close"
        />
      </Flex>
    </Modal>
  );
});
